import styled from '@emotion/styled'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { FadeInUp } from 'app/components/Common/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { Line } from 'app/components/Common/Line'
import { FlexBox } from 'app/components/Layout/FlexBox'
import React, { memo } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

export interface Props {
  cta?: ButtonProps
  description?: string
  image?: ImageProps
  subtitle?: string
  title?: string
}

export const Stripe = memo(function Stripe({
  cta,
  description,
  image,
  subtitle,
  title,
}: Props) {
  return (
    <Container>
      <Wrapper
        className="item-wrapper"
        dial={4}
        row
        space="between"
        stretch
        wrap
      >
        <LeftSide>
          {image ? (
            <LazyLoadComponent>
              <Image {...image} />
            </LazyLoadComponent>
          ) : null}
        </LeftSide>
        <RightSide dial={4}>
          {title ? (
            <FadeInUp>
              <Title>
                {title}
                {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
                <Line className="separator" />
              </Title>
            </FadeInUp>
          ) : null}
          {description ? (
            <FadeInUp>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            </FadeInUp>
          ) : null}
          {cta ? (
            <FadeInUp>
              <CTA variant="advanced" {...cta} />
            </FadeInUp>
          ) : null}
        </RightSide>
      </Wrapper>
    </Container>
  )
})

const Container = styled.div`
  margin: 12.5rem 0;
  padding-top: 8.125rem;
  position: relative;
  &:before,
  &:after {
    content: '';
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    z-index: -1;
  }
  &:before {
    width: calc(50% + 15vw);
    height: calc(50% + 8.125rem);
    top: 0;
    left: 0;
  }
  &:after {
    width: 15.278vw;
    height: 15.278vw;
    bottom: 0;
    right: 0;
  }
  &:nth-of-type(even) {
    &:before {
      right: 0;
      left: auto;
    }
    &:after {
      right: auto;
      left: 0;
    }
    .item-wrapper {
      > div {
        &:first-of-type {
          order: 2;
        }
        &:last-of-type {
          padding-right: 8.889vw;
          padding-left: 10.556vw;

          @media (max-width: 1199px) {
            padding-right: 2.5rem;
            padding-left: 1.875rem;
          }

          @media (max-width: 1023px) {
            order: 2;
            padding-right: 1.875rem;
          }

          @media (max-width: 767px) {
            padding-right: 1.25rem;
            padding-left: 1.25rem;
          }
        }
      }
    }
  }

  @media (max-width: 1199px) {
    margin: 7.5rem 0;
    padding-top: 5.625rem;
    &:before {
      width: calc(50% + 12vw);
    }
  }

  @media (max-width: 1023px) {
    margin: 5.625rem 0;
    padding-top: 0;
    &:before,
    &:after {
      display: none;
    }
  }
`

const Wrapper = styled(FlexBox)``

const LeftSide = styled.div`
  width: calc(50% - 1.042vw);
  background: ${({ theme }) => theme.colors.variants.neutralLight2};
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 1023px) {
    width: calc(100% - 3.75rem);
    margin: 0 auto;
    padding-bottom: 50%;
  }

  @media (max-width: 767px) {
    width: calc(100% - 2.5rem);
    padding-bottom: 66%;
  }
`

const RightSide = styled(FlexBox)`
  width: 50%;
  padding: 5.625rem 10.556vw 5.625rem 8.889vw;

  @media (max-width: 1199px) {
    padding: 3.75rem 1.875rem 3.75rem 2.5rem;
  }

  @media (max-width: 1023px) {
    width: 100%;
    padding-right: 1.875rem;
    padding-left: 1.875rem;
  }

  @media (max-width: 767px) {
    padding: 1.875rem 1.25rem 0;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.25rem;
  font-weight: 600;
  line-height: 2.875rem;

  .separator {
    margin-top: 0.625rem;
  }
`

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.25rem;
  font-weight: 400;
  line-height: 2.875rem;
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1.0625rem;
  line-height: 1.875rem;
  margin-top: 3.75rem;

  @media (max-width: 1199px) {
    font-size: 1rem;
    line-height: 1.75rem;
    margin-top: 3rem;
  }
`

const CTA = styled(Button)``
